// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// ASP #13 — the questions written on this therapy's drug record (Pharmacy settings → Drug list →
// "Drug specific assessment"), asked in Clinical Review. Each answer is saved on its own (mig 436,
// append-only — an earlier answer is kept, never overwritten). An answer that differs from the drug
// record's expected answer is flagged for the pharmacist; nothing here blocks the review.

import { useEffect, useRef, useState } from 'react'
import DateInput from '../../components/ui/DateInput'
import SearchableSelect from '../../components/common/SearchableSelect'
import { formatUsDateTime } from '../../api/format'
import { friendlyError } from '../../api/errors'
import type { DrugAssessmentItem } from '../../api/carerxClinical'
import { useAnswerDrugAssessment, useDrugAssessment } from '../../hooks/queries/carerxClinical'

const box = { border: '1px solid #dde5ec', borderRadius: 12, padding: 15, marginTop: 14 } as const
const input = { fontSize: 12.5, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)' } as const
const save = { fontSize: 12, fontWeight: 600, padding: '5px 12px', borderRadius: 7, border: 'none', background: 'var(--color-primary)', color: '#fff', cursor: 'pointer' } as const

export default function DrugAssessmentCard({ therapyId }: { therapyId: string }) {
  const q = useDrugAssessment(therapyId)
  if (q.isLoading) return null
  if (q.isError) return <section style={box} aria-label="Drug specific assessment"><div style={{ fontSize: 12, color: '#b23a48' }}>{friendlyError(q.error)}</div></section>
  const d = q.data!
  // A drug with no questions of its own adds nothing to the review — no empty card.
  if (d.items.length === 0) return null
  return (
    <section style={box} aria-label="Drug specific assessment">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 660 }}>Drug specific assessment</div>
        <span style={{ fontSize: 11.5, color: '#8496a5' }}>{d.items.length} question{d.items.length === 1 ? '' : 's'} from the drug record</span>
        {d.required_unanswered > 0 && <span role="status" style={{ fontSize: 11.5, fontWeight: 600, color: '#92610f' }}>{d.required_unanswered} required not answered yet</span>}
        {d.flagged > 0 && <span role="status" style={{ fontSize: 11.5, fontWeight: 600, color: '#b45309' }}>{d.flagged} outside the expected answer</span>}
      </div>
      {d.items.map((it, i) => <Question key={it.id} n={i + 1} item={it} therapyId={therapyId} />)}
    </section>
  )
}

function Question({ n, item, therapyId }: { n: number; item: DrugAssessmentItem; therapyId: string }) {
  const answer = useAnswerDrugAssessment(therapyId)
  const stored = item.assessment_answer ?? ''
  const [draft, setDraft] = useState(stored)
  const [err, setErr] = useState<string | null>(null)
  // S9 (review 2026-10-02): the box follows the STORED answer — the server normalises (1.50 → 1.5,
  // multi-select in the drug record's order), so the draft re-syncs instead of staying "unsaved".
  // ...but only over a draft that still shows the previous stored answer or the one just submitted:
  // text typed after Save, before the refetch lands, is the user's and stays.
  const prevStored = useRef(stored)
  const submitted = useRef<string | null>(null)
  useEffect(() => {
    setDraft(d => (d.trim() === prevStored.current || d.trim() === submitted.current ? stored : d))
    prevStored.current = stored
  }, [stored])
  const dirty = draft.trim() !== stored && draft.trim() !== ''
  const submit = (value = draft) => {
    if (value.trim() === stored) return   // re-clicking the current answer is not a new answer
    submitted.current = value.trim()
    setErr(null)
    answer.mutate({ questionId: item.id, answer: value }, { onError: e => setErr(friendlyError(e)) })
  }
  const label = `Answer to question ${n}`
  return (
    <div style={{ borderTop: '1px solid var(--color-border)', padding: '9px 0', background: item.outside_expected ? '#fffbeb' : undefined }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 12.5 }}>
        <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11, color: '#8496a5' }}>Q{n}</span>
        <b style={{ flex: 1, minWidth: 200 }}>{item.question_text}</b>
        {item.required && <span style={{ fontSize: 10.5, fontWeight: 600, color: '#0a7a1d' }}>required</span>}
        <span style={{ fontSize: 10.5, color: '#8496a5' }}>{item.asked_when}</span>
      </div>
      {item.expected_answer && <div style={{ fontSize: 11.5, color: '#41566a', marginTop: 2 }}>Expected: <b>{item.expected_answer}</b></div>}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
        {item.question_type === 'Yes / No' ? (
          ['Yes', 'No'].map(v => (
            <button key={v} aria-pressed={item.assessment_answer === v} disabled={answer.isPending}
              onClick={() => { setDraft(v); submit(v) }}
              style={{ ...input, cursor: 'pointer', fontWeight: item.assessment_answer === v ? 700 : 500,
                       borderColor: item.assessment_answer === v ? 'var(--color-primary)' : 'var(--color-border)' }}>{v}</button>
          ))
        ) : (<>
          {item.question_type === 'Single select' ? (
            <SearchableSelect ariaLabel={label} value={draft} onChange={setDraft} placeholder="Choose…"
              options={item.options.map(o => ({ value: o, label: o }))} />
          ) : item.question_type === 'Multi select' ? (
            <span role="group" aria-label={label} style={{ display: 'inline-flex', gap: 10, flexWrap: 'wrap' }}>
              {item.options.map(o => {
                const set = draft.split(',').map(s => s.trim()).filter(Boolean)
                const on = set.includes(o)
                return (
                  <label key={o} style={{ fontSize: 12.5, display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                    <input type="checkbox" checked={on}
                      onChange={() => setDraft((on ? set.filter(x => x !== o) : [...set, o]).join(', '))} />{o}
                  </label>
                )
              })}
            </span>
          ) : item.question_type === 'Date' ? (
            <DateInput label={label} value={draft || null} onChange={v => setDraft(v ?? '')} />
          ) : (
            <input aria-label={label} value={draft} onChange={e => setDraft(e.target.value)}
              inputMode={item.question_type === 'Numeric' ? 'decimal' : undefined}
              style={{ ...input, minWidth: item.question_type === 'Numeric' ? 100 : 280 }} />
          )}
          <button style={{ ...save, opacity: dirty ? 1 : 0.5, cursor: dirty ? 'pointer' : 'default' }} disabled={!dirty || answer.isPending}
            onClick={() => submit()}>Save answer</button>
        </>)}
        {item.assessment_answer && (
          <span style={{ fontSize: 11, color: '#8496a5' }}>
            Answered {item.assessment_answer} · {item.answered_by_name || 'a user'} · {formatUsDateTime(item.answered_at)}
          </span>
        )}
      </div>
      {item.outside_expected && (
        <div role="status" style={{ fontSize: 11.5, fontWeight: 600, color: '#b45309', marginTop: 4 }}>
          Outside the expected answer — {item.out_of_expected.toLowerCase()}.
        </div>
      )}
      {err && <div style={{ fontSize: 11.5, color: '#b23a48', marginTop: 4 }}>{err}</div>}
    </div>
  )
}
